<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-row>
        <b-col cols="12" xl="6">
          <b-card>
            <div slot="header">{{ 'structure_update_basic_info_title' | translate('global') }}</div>
            <div v-if="spinners.contentIsLoading" class="text-center">
              <b-spinner type="grow" variant="primary"/>
            </div>
            <div v-else>
              <b-form-group :label="'structure' | translate('global')">
                <treeselect value-format="object"  :placeholder=" 'select_structure' | translate('global') "
                            :class="{ 'treeselect-error' : errorMsg.departments_classifier_id }"
                            :options="structureOptions" v-model="selectedStructure"/>
                <span class="error-text" v-if="errorMsg.sites_classifier_id">
                   {{ errorMsg.sites_classifier_id[0] }}
                  </span>
              </b-form-group>
              <b-form-group>
                <label>{{ 'title' | translate('global') }}</label>
                <b-form-input type="text" :placeholder=" 'structure_title_placeholder' | translate('global') "
                              :class="{ 'is-invalid' : errorMsg.name }"
                              v-model="structure.name"/>
                <div class="invalid-feedback" v-if="errorMsg.name">{{ errorMsg.name[0] }}</div>
              </b-form-group>
              <BudgetLimits :limits="nativeBudgetLimits"
                            :editable="editableLimits"
                            :errorMsg="errorMsg"
                            @budgetLimit="onBudgetLimitChange($event)">
              </BudgetLimits>
            </div>
            <div slot="footer">
              <b-button type="submit" variant="success" :disabled="spinners.isSaving" @click="onSave()">
                <b-spinner v-if="spinners.isSaving" type="grow" small/>
                {{ 'save' | translate('global') }}
              </b-button>
              <b-button :to="{ name: 'Structures' }" type="reset" variant="secondary" class="ml-2">
                {{ 'back_to_list' | translate('global') }}
              </b-button>
            </div>
          </b-card>
        </b-col>

        <b-col cols="12" xl="6">
          <b-card>
            <div slot="header">{{ 'structure_update_users_title' | translate('global') }}</div>
            <div v-if="spinners.contentIsLoading" class="text-center">
              <b-spinner type="grow" variant="primary"/>
            </div>
            <div v-else>
              <AddUser :roleOptions="roleOptions" :errorMsg="errorMsg"></AddUser>
              <div v-if="tableItems.length">
                <hr>
                <UsersList :tableItems="tableItems"></UsersList>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import statuses from '@/store/statuses';
import TreeselectMixin from '@/mixins/TreeselectMixin';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import forEach from 'lodash.foreach';
import { mapGetters, mapActions } from 'vuex';
import VuexTypes from '@/store/types/index';
import BudgetLimits from '../BudgetLimits.vue';
import AddUser from './AddUser.vue';
import UsersList from './UsersList.vue';

const StructureTypes = VuexTypes.structures;
export default {
  mounted() {
    this.getData();
  },
  mixins: [TreeselectMixin],
  data() {
    return {
      spinners: {
        contentIsLoading: true,
        isSaving: false,
      },
      structure: {
        departments_classifier_id: null,
        name: '',
      },
      structureOptions: [],
      selectedStructure: null,
      budgetLimits: {},
      nativeBudgetLimits: {},
      editableLimits: {},
      roleOptions: [],
      tableItems: [],
      errorMsg: {},
    };
  },
  components: {
    UsersList,
    Treeselect,
    BudgetLimits,
    AddUser
  },
  computed: {
    ...mapGetters('Structures', [
      'structureUpdateData',
      'structureUpdate',
      'addUser',
      'deleteUser',
    ]),
  },
  watch: {
    structureUpdateData: {
      handler: function () {
        if (this.structureUpdateData.status === statuses.LOADED) {
          this.fillData();
          this.spinners.contentIsLoading = false;
        }
      },
      deep: true
    },
    structureUpdate: {
      handler: function () {
        this.errorMsg = {};
        this.spinners.isSaving = false;
        if (this.structureUpdate.status === statuses.LOADED) {
          this.alertSuccess();
          this.getData();
        } else if (this.structureUpdate.status === statuses.ERROR) {
          this.alertError();
          this.errorMsg = this.structureUpdate.data.data.errors;
        }
      },
      deep: true
    },
    addUser: {
      handler: function () {
        if (this.addUser.status === statuses.LOADED) {
          this.getData();
        }
      },
      deep: true
    },
    deleteUser: {
      handler: function () {
        if (this.deleteUser.status === statuses.LOADED) {
          this.getData();
        }
      },
      deep: true
    },
    selectedStructure: function () {
      this.structure['departments_classifier_id'] = this.selectedStructure ? this.selectedStructure['id'] : null;
    },
  },
  methods: {
    ...mapActions('Structures', [
      StructureTypes.STRUCTURE_UPDATE_DATA,
      StructureTypes.STRUCTURE_UPDATE,
    ]),
    getData() {
      this.STRUCTURE_UPDATE_DATA(this.$route.params.id);
    },
    fillData() {
      this.structureOptions = this.fillTreeselectDropdown(
        this.structureUpdateData.data['departments_classifiers'], 'id', 'name'
      );
      const structure = this.structureUpdateData.data.structure ? this.structureUpdateData.data.structure : {};
      this.structure.name = structure.name;
      this.structure.departments_classifier_id = structure.departments_classifier
        ? structure.departments_classifier.id : null;

      this.selectedStructure = structure.departments_classifier ? {
        id: structure.departments_classifier['id'],
        label: structure.departments_classifier['name']
      } : null;


      this.editableLimits = this.structureUpdateData.data.editableBudgetLimits;
      const limits = {};
      const items = [];
      forEach(structure, (item, key) => {
        const split = key.split('_');
        const isBudget = split[0] === 'budget';
        if (isBudget) {
          limits[key] = item;
        }
        if (key === 'users') {
          forEach(item, (user) => {
            const roleName = this.structureUpdateData.data.roles[user['structure_user_role']['role_id']].name;
            items.push({
              id: user['id'],
              full_name: user['full_name'],
              email: user['email'],
              role: roleName,
              delete: user['structure_user_role']
            });
          });
        }
      });
      this.budgetLimits = limits;
      this.nativeBudgetLimits = limits;
      this.tableItems = items;

      const roles = [];
      forEach(this.structureUpdateData.data.roles, (role) => {
        roles.push({
          id: role['id'],
          label: role['name']
        });
      });
      this.roleOptions = roles;
    },
    onSave() {
      this.spinners.isSaving = true;
      const saveData = { ...this.budgetLimits, ...this.structure };
      this.STRUCTURE_UPDATE({ id: this.$route.params.id, update: saveData });
    },
    onBudgetLimitChange(limits) {
      this.budgetLimits = limits;
    },
  }
};
</script>

<style scoped>
</style>
